import { Purchases, CustomerInfo } from "@revenuecat/purchases-js";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { logger } from "../services";

interface RevenueCatContextType {
  customerInfo: CustomerInfo | null;
  isLoading: boolean;
  isPlusPlan: boolean;
  error: string | null;
  refreshCustomerInfo: () => Promise<void>;
  checkEntitlement: (entitlementId: string) => Promise<boolean>;
}

const RevenueCatContext = createContext<RevenueCatContextType | undefined>(undefined);

interface RevenueCatProviderProps {
  children: React.ReactNode;
}

export const RevenueCatProvider: React.FC<RevenueCatProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user has the fitcheckr_plus entitlement
  const isPlusPlan = Boolean(
    customerInfo?.entitlements.active
    && customerInfo.entitlements.active["fitcheckr_plus"],
  );

  const refreshCustomerInfo = async () => {
    try {
      setError(null);
      const purchases = await Purchases.getSharedInstance();
      const info = await purchases.getCustomerInfo();
      setCustomerInfo(info);
    } catch (e) {
      await logger.error("customer_info_refresh_failed", e as Error);
      setError(e instanceof Error ? e.message : "Failed to fetch customer info");
      setCustomerInfo(null);
    }
  };

  const checkEntitlement = async (entitlementId: string): Promise<boolean> => {
    try {
      const purchases = await Purchases.getSharedInstance();
      const isEntitled = await purchases.isEntitledTo(entitlementId);
      return isEntitled;
    } catch (e) {
      await logger.error("entitlement_check_failed", e as Error, {
        entitlement_id: entitlementId,
      });
      return false;
    }
  };

  useEffect(() => {
    const initRevenueCat = async () => {
      // Wait for auth to initialize
      if (authLoading) {
        return;
      }

      const apiKey = process.env.REACT_APP_REVENUECAT_API_KEY;

      if (!apiKey) {
        await logger.error("revenuecat_api_key_missing", new Error("API key not configured"));
        setError("RevenueCat API key is not configured");
        setIsLoading(false);
        return;
      }

      try {
        setError(null);

        // Use Firebase user ID or generate anonymous ID
        const userId = user?.uid || Purchases.generateRevenueCatAnonymousAppUserId();

        // Configure RevenueCat with Firebase user ID
        Purchases.configure({
          apiKey,
          appUserId: userId,
        });

        // Get the shared instance for preloading and customer info
        const purchases = await Purchases.getSharedInstance();

        // Preload offerings and customer info for better performance
        await purchases.preload();

        // Get initial customer info
        const info = await purchases.getCustomerInfo();
        setCustomerInfo(info);
      } catch (e) {
        await logger.error("revenuecat_configuration_failed", e as Error);
        setError(e instanceof Error ? e.message : "Failed to configure RevenueCat");
      } finally {
        setIsLoading(false);
      }
    };

    initRevenueCat();
  }, [authLoading, user?.uid]);

  // Handle user changes (login/logout)
  useEffect(() => {
    const handleUserChange = async () => {
      if (authLoading || isLoading) {
        return;
      }

      try {
        const newUserId = user?.uid || Purchases.generateRevenueCatAnonymousAppUserId();

        // Get shared instance and change user
        const purchases = await Purchases.getSharedInstance();
        const updatedCustomerInfo = await purchases.changeUser(newUserId);
        setCustomerInfo(updatedCustomerInfo);
      } catch (e) {
        await logger.error("revenuecat_user_change_failed", e as Error);
        setError(e instanceof Error ? e.message : "Failed to update user");
      }
    };

    // Only run this effect if RevenueCat is already initialized (not loading)
    if (!isLoading) {
      handleUserChange();
    }
  }, [user?.uid, isLoading, authLoading]);

  const value: RevenueCatContextType = {
    customerInfo,
    isLoading,
    isPlusPlan,
    error,
    refreshCustomerInfo,
    checkEntitlement,
  };

  return (
    <RevenueCatContext.Provider value={value}>
      {children}
    </RevenueCatContext.Provider>
  );
};

export const useRevenueCat = (): RevenueCatContextType => {
  const context = useContext(RevenueCatContext);
  if (context === undefined) {
    throw new Error("useRevenueCat must be used within a RevenueCatProvider");
  }
  return context;
};

export default RevenueCatProvider;
